import { useEffect, useRef, useState } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import { floatingDockOffset } from '~/modules/root/util/floating-dock';
import { useFixedUIHidden } from '~/modules/root/hook/use-fixed-ui-hidden';
import { useFeature } from '~/modules/root/hook/use-feature';

/** Kill-switch shared with the control this replaces. */
const BACK_TO_TOP_FEATURE = 'back_to_top_button';

/**
 * Scroll distance before the control appears. A full viewport is far enough
 * that the page top is genuinely out of reach, and short enough that it is
 * there when wanted on a long grid.
 */
const REVEAL_AFTER_VIEWPORTS = 1;

interface BackToTopProps {
    /**
     * Page theme. On the dark editorial pages the control inverts to a white
     * fill, matching the other floating chrome.
     */
    isDark?: boolean;
}

/**
 * Floating "back to top" control for long scrolling pages, desktop only.
 *
 * Sits in the bottom-right corner, stacking above the chat dock when one is on
 * screen and dropping to the viewport floor when there is none — the dock
 * publishes its own height, so this makes no assumption about who is logged in.
 *
 * Reads the same `back_to_top_button` flag as the control it replaces, so the
 * kill-switch covers both while the two surfaces coexist.
 */
export const BackToTop = ({ isDark = false }: BackToTopProps) => {
    const [visible, setVisible] = useState(false);
    const isFixedUIHidden = useFixedUIHidden();
    const tracker = useTracker();
    const enabled = useFeature(BACK_TO_TOP_FEATURE);
    const frameRef = useRef(0);

    useEffect(() => {
        const measure = () => {
            frameRef.current = 0;
            setVisible(
                window.scrollY > window.innerHeight * REVEAL_AFTER_VIEWPORTS,
            );
        };
        // Coalesce to one measurement per frame: both handlers read layout, and
        // a scroll fires far more often than the threshold can change.
        const schedule = () => {
            if (frameRef.current) return;
            frameRef.current = window.requestAnimationFrame(measure);
        };
        measure();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule, { passive: true });
        return () => {
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
        };
    }, []);

    const onClick = () => {
        window.scrollTo({
            top: 0,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)')
                .matches
                ? 'auto'
                : 'smooth',
        });
        tracker.log('BackToTopButtonClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
        });
    };

    if (!enabled) return null;

    return (
        <button
            type="button"
            onClick={onClick}
            aria-label="Back to top"
            // Hidden rather than unmounted so the fade runs both ways;
            // aria-hidden + inert keep it off the tab order while out.
            aria-hidden={!visible}
            inert={!visible}
            className={cn(
                // Desktop only: the dock this clears never renders below md, so
                // the offset would collapse onto the mobile floating chrome.
                'max-md:hidden',
                'fixed right-4 z-fixed flex size-[2.625rem] items-center justify-center rounded-full',
                'transition-[opacity,transform] duration-300 ease-out',
                visible
                    ? 'translate-y-0 opacity-100'
                    : 'pointer-events-none translate-y-2 opacity-0',
                isDark ? 'bg-white text-[#141414]' : 'bg-[#141414] text-white',
                isFixedUIHidden && 'hidden',
            )}
            style={{ bottom: floatingDockOffset() }}
        >
            {/* Chevron rather than a full arrow: the smaller mark reads
                cleanly at this size and matches the pill-bar chevrons. */}
            <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                className="size-[1.1rem]"
            >
                <path
                    d="M6 14.5L12 8.5L18 14.5"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </button>
    );
};
